import { useQueryClient } from '@tanstack/react-query'
import { eventImagesApi } from '~/entities/event/api'
import { CoverFitButton } from '~/features/ai-studio/components'
import { CoverChatButton } from '~/features/events/ai-flow/cover-chat-button'
import type { CoverSubject } from '~/features/events/ai-flow/event-ai-chat'
import {
  type ExistingImage,
  type ImageFile,
  ImagesSection,
} from '~/features/shared/components/images-section'
import * as m from '~/paraglide/messages'
import { QueryKey, queryKey } from '~/shared/lib/query-keys'

interface EventImagesSectionProps {
  eventId?: string
  existingImages?: ExistingImage[]
  initialNewImages?: ImageFile[]
  onImagesChange?: (files: ImageFile[], primaryIndex: number) => void
  onRemovedExistingChange?: (imageIds: string[]) => void
  /** The event the cover chat designs for, read when it opens. */
  coverSubject?: () => CoverSubject
}

export function EventImagesSection({
  eventId,
  existingImages = [],
  initialNewImages = [],
  onImagesChange,
  onRemovedExistingChange,
  coverSubject,
}: EventImagesSectionProps) {
  const queryClient = useQueryClient()

  const handleDeleteExisting = async (imageId: string) => {
    if (!eventId) throw new Error('Event ID is required')
    await eventImagesApi.delete(imageId)
    queryClient.invalidateQueries({
      queryKey: queryKey(QueryKey.EVENT, eventId),
    })
  }

  const handleReorderExisting = async (imageIds: string[]) => {
    if (!eventId) throw new Error('Event ID is required')
    await eventImagesApi.reorder(eventId, imageIds)
    queryClient.invalidateQueries({
      queryKey: queryKey(QueryKey.EVENT, eventId),
    })
  }

  return (
    <div data-field="images">
      <ImagesSection
        title={m.eventImages()}
        description={m.uploadImagesForYourEventTheFirstImageIsAlwaysTheCoverImageDragToReorderOrClickSetPrimaryToMoveAnImageToTheFirstPosition()}
        entityId={eventId}
        existingImages={existingImages}
        initialNewImages={initialNewImages}
        onImagesChange={onImagesChange}
        onRemovedExistingChange={onRemovedExistingChange}
        onDeleteExisting={handleDeleteExisting}
        onReorderExisting={handleReorderExisting}
        actions={(_addImage, cover) => (
          <>
            <CoverChatButton onCover={cover.put} subject={coverSubject} />
            <CoverFitButton cover={cover} />
          </>
        )}
      />
    </div>
  )
}
